import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DevicesService, MeasurementsService } from '@cosmos/angular/api';
import { RBACService, StateService } from '@cosmos/angular/utils';
import { Device, Measurement, Project, User } from '@cosmos/shared';
import { ErrorService } from '@cosmos/angular/utils';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EditMeasurementForm, MeasurementChangeDeviceForm } from '../../dynamic-form/forms/measurement.form';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import { CorrosionInsightsCardComponent } from '../../cards/insights/corrosion-insights-card/corrosion-insights-card.component';
import { DatePipe } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DynamicLineChartComponent } from '../../data/charts/dynamic-line-chart/dynamic-line-chart.component';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';


@Component({
  selector: 'cosmos-safecor-measurement',
  templateUrl: './safecor-measurement.component.html',
  imports: [
    DatePipe, ButtonModule, ConfirmDialogModule, DynamicDialogModule,
    DividerModule, CardModule, TranslatePipe, CorrosionInsightsCardComponent, DynamicLineChartComponent
  ],
  providers: [ConfirmationService, DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafecorMeasurementComponent {
  private readonly errorService = inject(ErrorService);
  readonly rbac = inject(RBACService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly dialogService = inject(DialogService);
  private readonly translateService = inject(TranslateService);
  private readonly measurementsService = inject(MeasurementsService);
  private readonly devicesService = inject(DevicesService);
  private readonly stateService = inject(StateService);

  readonly measurementId = input<string>();
  readonly data = input<string>();

  // Signals — mutations trigger OnPush re-render
  readonly measurement = signal<Measurement | null>(null);
  readonly device = signal<Device | null>(null);
  readonly project = signal<Project | null>(null);
  readonly corrosionData = signal<{
    potential: any[]; rp: any[]; rohm: any[]; temperature: any[];
    last_ice: any[]; quality: any[]; humidity: any[]; time: any[];
  }>({
    potential: [], rp: [], rohm: [], temperature: [],
    last_ice: [], quality: [], humidity: [], time: []
  });

  user: User;

  constructor() {
    this.stateService.currentUser$
      .pipe(takeUntilDestroyed())
      .subscribe(x => { this.user = x; });

    effect(() => {
      if (this.measurementId()) {
        this.initData();
      }
    });
  }

  initData(): void {
    this.measurementsService.findById(this.measurementId(), '/?populate_project&populate_device=name,deviceProfile').subscribe({
      next: (response) => {
        this.project.set(response.body.project);
        this.measurement.set(response.body);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => this.getData(),
    });
  }

  getData() {
    const m = this.measurement();
    this.device.set(m.device as Device || { name: '-' });

    // Collect all data points from both electrodes
    const allData = [
      ...(m.data.electrode1.rangeData || []),
      ...(m.data.electrode2.rangeData || [])
    ];

    // Sort all data points by their timestamp
    allData.sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    const potential: any[] = [], rp: any[] = [], rohm: any[] = [], temperature: any[] = [];
    const last_ice: any[] = [], quality: any[] = [], humidity: any[] = [], time: any[] = [];

    allData.forEach(data => {
      potential.push(data.potential);
      rp.push(data.rp);
      rohm.push(data.rohm);
      temperature.push(data.temperature);
      last_ice.push(data.last_ice);
      quality.push(data.quality);
      humidity.push(data.humidity);
      time.push(data.time);
    });

    this.corrosionData.set({ potential, rp, rohm, temperature, last_ice, quality, humidity, time });
  }

  editMeasurement() {
    this.openDialog();
  }

  finishMeasurement($event: Event) {
    this.confirmationService.confirm({
      target: $event.currentTarget as EventTarget,
      message: this.translateService.instant('measurement.finish-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: this.translateService.instant('button.finish')
      },
      accept: () => {
        this.measurementsService.finish(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.finishSuccess',
            onSuccess: () => this.getData(),
          }),
        );
      },
      reject: () => null
    });
  }

  changeDevice(measurement: Measurement) {
    this.devicesService.find(`/?project=${this.measurement().project._id}&available=true&deviceType=DataAcquisition&deviceProfile=${this.device().deviceProfile}`).subscribe({
      next: (response) => {
        const devices = response.body;
        if (devices.length > 0) {
          const measurementChangeDeviceForm = new MeasurementChangeDeviceForm(this.translateService, devices, this.user, {});
          const dialogRef = this.dialogService.open(DynamicFormDialog, {
            data: { form: measurementChangeDeviceForm },
            header: this.translateService.instant('measurement.change_device'),
            modal: true, closable: true, width: '50vw',
            breakpoints: { '960px': '75vw', '640px': '90vw' },
          });
          dialogRef.onClose.subscribe(result => {
            if (result !== undefined) {
              this.measurementsService.changeDevice(this.measurement()._id, result).subscribe(
                this.errorService.handleMutation({
                  successMessageKey: 'measurement.messages.updateSuccess',
                  onSuccess: () => this.getData(),
                }),
              );
            }
          });
        } else {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
      error: (e) => {
        if (e.error.message == 'Devices not Found') {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
    });
  }

  changeDeviceSender(device: Device) {
    this.measurementsService.changeDevice(this.measurement()._id, device).subscribe(
      this.errorService.handleMutation({
        successMessageKey: 'measurement.messages.updateSuccess',
        onSuccess: () => this.getData(),
      }),
    );
  }

  openDialog() {
    const m = this.measurement();
    const editMeasurementForm = new EditMeasurementForm(this.translateService, m, this.user, {
      visualizationConfig: this.project().visualizationConfig, measurementType: m.measurementType
    });
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm, dialogTitle: this.translateService.instant('dialog.title.edit.measurement') },
      header: this.translateService.instant('dialog.title.edit.measurement'),
      modal: true, closable: true, width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe(result => {
      if (result !== undefined) {
        this.measurementsService.update([m._id, result]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.getData(),
          }),
        );
      }
    });
  }
}
